import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/text-input";

/**
 * Workshop: Agents section for Settings.
 *
 * List view with drill-down to agent detail editor.
 * Agent editor sections:
 *   1. Identity — name, role, capabilities, system prompt
 *   2. Model & Runtime — model, thinking effort, max turns
 *   3. Tools & Skills — MCP server toggles, assigned skills
 *   4. Run Policy — heartbeat toggle + interval
 *   5. Advanced — timeout, environment variables (collapsible)
 */

// ─── Types ──────────────────────────────────────────────────────────────────

interface AgentTool {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
}

interface AgentSkill {
  id: string;
  name: string;
  description: string;
}

interface EnvVar {
  key: string;
  value: string;
}

interface Agent {
  id: string;
  name: string;
  role: string;
  capabilities: string;
  systemPrompt: string;
  model: string;
  thinkingEffort: "auto" | "high" | "low";
  maxTurns: number;
  tools: AgentTool[];
  skills: AgentSkill[];
  heartbeatEnabled: boolean;
  heartbeatInterval: number;
  timeout: number;
  envVars: EnvVar[];
  status: "active" | "paused";
  color: string;
  letter: string;
}

// ─── Mock Data ──────────────────────────────────────────────────────────────

const MOCK_TOOLS: AgentTool[] = [
  { id: "canvas", name: "denker-canvas", description: "Create and update frames on canvas", enabled: true },
  { id: "workflow", name: "denker-workflow", description: "Create and manage workflows", enabled: true },
  { id: "memory", name: "denker-memory", description: "Read and write knowledge graph", enabled: true },
  { id: "bridge", name: "denker-bridge", description: "Companion bridge communication", enabled: true },
  { id: "gmail", name: "gmail", description: "Send and read emails", enabled: false },
  { id: "slack", name: "slack", description: "Post messages to Slack channels", enabled: false },
  { id: "github", name: "github", description: "Manage repos, PRs, issues", enabled: false },
  { id: "notion", name: "notion", description: "Read and write Notion pages", enabled: false },
];

const MOCK_SKILLS: AgentSkill[] = [
  { id: "denker-skill", name: "Denker Skill", description: "Core agent protocol — frame creation, routing, memory" },
  { id: "research", name: "Deep Research", description: "Multi-source research with citations" },
  { id: "code-review", name: "Code Review", description: "Review PRs with structured feedback" },
];

const MOCK_AGENTS: Agent[] = [
  {
    id: "a1", name: "Coder", role: "Senior software engineer",
    capabilities: "Writes, reviews, and refactors code. Runs tests and debugging.",
    systemPrompt: "You are a senior software engineer. Write clean, efficient, well-tested code. Follow the project's coding standards. Always explain your approach before implementing.",
    model: "sonnet", thinkingEffort: "auto", maxTurns: 80,
    tools: MOCK_TOOLS.map((t) => ({ ...t, enabled: ["canvas", "workflow", "bridge", "github"].includes(t.id) })),
    skills: [MOCK_SKILLS[0]!, MOCK_SKILLS[2]!],
    heartbeatEnabled: false, heartbeatInterval: 3600, timeout: 0,
    envVars: [], status: "active", color: "#30D158", letter: "C",
  },
  {
    id: "a2", name: "Researcher", role: "Research analyst",
    capabilities: "Web search, document analysis, competitive intelligence, fact-checking.",
    systemPrompt: "You are a thorough researcher. Verify facts, cite sources, and present findings in structured format. Cross-reference multiple sources before concluding.",
    model: "opus", thinkingEffort: "high", maxTurns: 120,
    tools: MOCK_TOOLS.map((t) => ({ ...t, enabled: ["canvas", "workflow", "memory", "bridge"].includes(t.id) })),
    skills: [MOCK_SKILLS[0]!, MOCK_SKILLS[1]!],
    heartbeatEnabled: true, heartbeatInterval: 3600, timeout: 300,
    envVars: [], status: "active", color: "#60A5FA", letter: "R",
  },
  {
    id: "a3", name: "Writer", role: "Content strategist",
    capabilities: "Blog posts, emails, newsletters, social media copy, documentation.",
    systemPrompt: "You are a creative writer with a professional tone. Match the brand voice. Draft, iterate, and polish content.",
    model: "sonnet", thinkingEffort: "auto", maxTurns: 60,
    tools: MOCK_TOOLS.map((t) => ({ ...t, enabled: ["canvas", "bridge", "gmail", "slack"].includes(t.id) })),
    skills: [MOCK_SKILLS[0]!],
    heartbeatEnabled: false, heartbeatInterval: 3600, timeout: 0,
    envVars: [], status: "paused", color: "#A78BFA", letter: "W",
  },
];

// Workshop uses tier aliases so the label tracks whatever model the
// companion currently resolves them to — no manual bumps needed when
// Anthropic ships a new version.
const MODEL_OPTIONS = [
  { value: "opus", label: "Claude Opus 4.8" },
  { value: "sonnet", label: "Claude Sonnet 4.6" },
  { value: "haiku", label: "Claude Haiku 4.5" },
];

const THINKING_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "high", label: "High" },
  { value: "low", label: "Low" },
];

const ALL_AVAILABLE_SKILLS: AgentSkill[] = [
  ...MOCK_SKILLS,
  { id: "email-draft", name: "Email Drafting", description: "Draft emails with approval gate" },
  { id: "data-analysis", name: "Data Analysis", description: "Analyze data with benchmarks" },
  { id: "workflow-exec", name: "Workflow Execution", description: "Execute multi-step workflows" },
];

// ─── Shared Helpers ─────────────────────────────────────────────────────────

function FieldGroup({ label, hint, children, className, "data-testid": testId }: {
  label: string; hint?: string; children: React.ReactNode; className?: string; "data-testid"?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)} data-testid={testId}>
      <div className="flex items-center gap-1">
        <label className="text-[10px] font-medium text-secondary">{label}</label>
        {hint && (
          <span className="text-[9px] text-muted" title={hint}>
            <Icons.HelpCircle className="h-3 w-3" />
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-primary">
      {children}
    </h3>
  );
}

function ToggleSwitch({ enabled, onToggle, "data-testid": testId }: {
  enabled: boolean; onToggle: () => void; "data-testid"?: string;
}) {
  return (
    <button
      type="button" role="switch" aria-checked={enabled} onClick={onToggle}
      className={cn(
        "relative h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors",
        enabled ? "bg-accent" : "border border-glass-stroke-light bg-glass-fill-heavy",
      )}
      data-testid={testId}
    >
      <span className={cn(
        "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-[left]",
        enabled ? "left-[18px]" : "left-0.5",
      )} />
    </button>
  );
}

function SelectField({ value, options, onChange, testId }: {
  value: string; options: { value: string; label: string }[]; onChange: (v: string) => void; testId: string;
}) {
  return (
    <select
      value={value} onChange={(e) => onChange(e.target.value)}
      className="h-[34px] w-full rounded-lg border border-glass-stroke-light bg-glass-fill-heavy px-3 text-[10px] text-primary backdrop-blur-glass-sm focus:border-accent focus:outline-none"
      data-testid={testId}
    >
      {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  );
}

// ─── Agent List View ────────────────────────────────────────────────────────

function AgentList({ agents, onSelect, onCreateAgent, "data-testid": testId }: {
  agents: Agent[]; onSelect: (id: string) => void; onCreateAgent: () => void; "data-testid"?: string;
}) {
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {agents.map((agent) => (
        <button
          key={agent.id} type="button" onClick={() => onSelect(agent.id)}
          className="flex items-center gap-3 rounded-lg border border-glass-stroke-subtle p-3 transition-colors hover:border-glass-stroke-light"
          data-testid={`agent-row-${agent.id}`}
        >
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
            style={{ backgroundColor: agent.color }}
          >
            {agent.letter}
          </div>
          <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-primary">{agent.name}</span>
              <Badge variant={agent.status === "active" ? "default" : "outline"}>
                {agent.status}
              </Badge>
            </div>
            <span className="truncate text-[10px] text-secondary">{agent.role}</span>
          </div>
          <span className="hidden text-[10px] text-muted sm:block">
            {MODEL_OPTIONS.find((m) => m.value === agent.model)?.label ?? agent.model}
          </span>
          <Icons.ChevronRight className="h-4 w-4 shrink-0 text-muted" />
        </button>
      ))}
      <Button variant="secondary" size="sm" onClick={onCreateAgent} data-testid="agent-create-btn">
        <Icons.Plus className="h-3.5 w-3.5" />
        Create agent
      </Button>
    </div>
  );
}

// ─── Agent Detail Editor ────────────────────────────────────────────────────

function AgentDetail({ agent, onBack, onSave, onDelete, "data-testid": testId }: {
  agent: Agent; onBack: () => void; onSave: (updated: Agent) => void; onDelete: (id: string) => void; "data-testid"?: string;
}) {
  const [draft, setDraft] = useState<Agent>({ ...agent, tools: agent.tools.map((t) => ({ ...t })), skills: [...agent.skills], envVars: agent.envVars.map((e) => ({ ...e })) });
  const [showAdvanced, setShowAdvanced] = useState(false);

  function update<K extends keyof Agent>(key: K, value: Agent[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function toggleTool(toolId: string) {
    setDraft((prev) => ({
      ...prev,
      tools: prev.tools.map((t) => t.id === toolId ? { ...t, enabled: !t.enabled } : t),
    }));
  }

  function removeSkill(skillId: string) {
    setDraft((prev) => ({ ...prev, skills: prev.skills.filter((s) => s.id !== skillId) }));
  }

  function addSkill(skill: AgentSkill) {
    if (!draft.skills.find((s) => s.id === skill.id)) {
      setDraft((prev) => ({ ...prev, skills: [...prev.skills, skill] }));
    }
  }

  function addEnvVar() {
    setDraft((prev) => ({ ...prev, envVars: [...prev.envVars, { key: "", value: "" }] }));
  }

  function updateEnvVar(index: number, field: "key" | "value", val: string) {
    setDraft((prev) => ({
      ...prev,
      envVars: prev.envVars.map((e, i) => i === index ? { ...e, [field]: val } : e),
    }));
  }

  function removeEnvVar(index: number) {
    setDraft((prev) => ({ ...prev, envVars: prev.envVars.filter((_, i) => i !== index) }));
  }

  const unassignedSkills = ALL_AVAILABLE_SKILLS.filter((s) => !draft.skills.find((ds) => ds.id === s.id));

  return (
    <div className="flex flex-col gap-5" data-testid={testId}>
      {/* Back */}
      <button type="button" onClick={onBack}
        className="flex items-center gap-1 text-[10px] text-secondary transition-colors hover:text-primary"
        data-testid="agent-detail-back-btn"
      >
        <Icons.ArrowLeft className="h-3.5 w-3.5" />
        Back to agents
      </button>

      {/* ── Section 1: Identity ── */}
      <SectionHeader>Identity</SectionHeader>

      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
          style={{ backgroundColor: draft.color }}
        >
          {draft.letter}
        </div>
        <FieldGroup label="Name" className="flex-1">
          <TextInput value={draft.name} onChange={(e) => update("name", e.target.value)} data-testid="agent-name-input" />
        </FieldGroup>
      </div>

      <FieldGroup label="Role" hint="Short description shown in agent lists">
        <TextInput value={draft.role} onChange={(e) => update("role", e.target.value)}
          placeholder="e.g. Senior software engineer" data-testid="agent-role-input" />
      </FieldGroup>

      <FieldGroup label="Capabilities" hint="What this agent is good at — shown in agent picker">
        <TextInput value={draft.capabilities} onChange={(e) => update("capabilities", e.target.value)}
          placeholder="Describe what this agent can do..." data-testid="agent-capabilities-input" />
      </FieldGroup>

      <FieldGroup label="System prompt" hint="Markdown instructions defining the agent's persona and behavior">
        <textarea
          value={draft.systemPrompt} onChange={(e) => update("systemPrompt", e.target.value)}
          rows={8} placeholder="You are agent {{ agent.name }}. Your role is {{ agent.role }}..."
          className="w-full rounded-lg border border-glass-stroke-light bg-glass-fill-heavy p-3 font-mono text-[10px] text-primary backdrop-blur-glass-sm placeholder:text-muted focus:border-accent focus:outline-none"
          data-testid="agent-prompt-textarea"
        />
      </FieldGroup>

      {/* ── Section 2: Model & Runtime ── */}
      <SectionHeader>Model &amp; Runtime</SectionHeader>

      <FieldGroup label="Model">
        <SelectField value={draft.model} options={MODEL_OPTIONS} onChange={(v) => update("model", v)} testId="agent-model-select" />
      </FieldGroup>

      <FieldGroup label="Thinking effort" hint="How much the model reasons before responding">
        <SelectField value={draft.thinkingEffort} options={THINKING_OPTIONS}
          onChange={(v) => update("thinkingEffort", v as Agent["thinkingEffort"])} testId="agent-thinking-select" />
      </FieldGroup>

      <FieldGroup label="Max turns per run" hint="Maximum conversation turns before the agent stops">
        <TextInput type="number" value={String(draft.maxTurns)}
          onChange={(e) => update("maxTurns", parseInt(e.target.value, 10) || 80)}
          data-testid="agent-max-turns-input" />
      </FieldGroup>

      {/* ── Section 3: Tools & Skills ── */}
      <SectionHeader>Tools</SectionHeader>
      <p className="text-[9px] text-muted -mt-3">MCP servers this agent can access at runtime</p>

      <div className="flex flex-col gap-1.5">
        {draft.tools.map((tool) => (
          <div key={tool.id} className="flex items-center justify-between rounded-md border border-glass-stroke-subtle px-3 py-2" data-testid={`agent-tool-${tool.id}`}>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-medium text-primary">{tool.name}</span>
              <span className="text-[9px] text-muted">{tool.description}</span>
            </div>
            <ToggleSwitch enabled={tool.enabled} onToggle={() => toggleTool(tool.id)} data-testid={`agent-tool-toggle-${tool.id}`} />
          </div>
        ))}
      </div>

      <SectionHeader>Skills</SectionHeader>
      <p className="text-[9px] text-muted -mt-3">Skills define reusable behaviors and protocols</p>

      <div className="flex flex-col gap-1.5">
        {draft.skills.map((skill) => (
          <div key={skill.id} className="flex items-center justify-between rounded-md border border-glass-stroke-subtle px-3 py-2" data-testid={`agent-skill-${skill.id}`}>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] font-medium text-primary">{skill.name}</span>
              <span className="text-[9px] text-muted">{skill.description}</span>
            </div>
            <button type="button" onClick={() => removeSkill(skill.id)}
              className="flex h-5 w-5 items-center justify-center rounded text-muted transition-colors hover:bg-red-400/20 hover:text-red-400"
              data-testid={`agent-skill-remove-${skill.id}`} aria-label={`Remove ${skill.name}`}
            >
              <Icons.X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {unassignedSkills.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {unassignedSkills.map((skill) => (
              <button key={skill.id} type="button" onClick={() => addSkill(skill)}
                className="rounded-md border border-dashed border-glass-stroke-subtle px-2 py-1 text-[9px] text-muted transition-colors hover:border-accent hover:text-accent"
                data-testid={`agent-skill-add-${skill.id}`}
              >
                + {skill.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Section 4: Run Policy ── */}
      <SectionHeader>Run Policy</SectionHeader>

      <div className="flex items-center justify-between" data-testid="agent-heartbeat-section">
        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] font-medium text-primary">Heartbeat on interval</span>
          <span className="text-[9px] text-muted">Agent wakes up periodically to check for work</span>
        </div>
        <ToggleSwitch enabled={draft.heartbeatEnabled} onToggle={() => update("heartbeatEnabled", !draft.heartbeatEnabled)} data-testid="agent-heartbeat-toggle" />
      </div>

      {draft.heartbeatEnabled && (
        <div className="flex items-center gap-2 pl-0">
          <span className="text-[10px] text-secondary">Run heartbeat every</span>
          <TextInput type="number" value={String(draft.heartbeatInterval)}
            onChange={(e) => update("heartbeatInterval", parseInt(e.target.value, 10) || 3600)}
            className="w-20" data-testid="agent-heartbeat-interval-input" />
          <span className="text-[10px] text-muted">sec</span>
        </div>
      )}

      {/* ── Section 5: Advanced (collapsible) ── */}
      <button type="button" onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-1 text-[10px] font-medium text-secondary transition-colors hover:text-primary"
        data-testid="agent-advanced-toggle"
      >
        {showAdvanced ? <Icons.ChevronDown className="h-3.5 w-3.5" /> : <Icons.ChevronRight className="h-3.5 w-3.5" />}
        Advanced
      </button>

      {showAdvanced && (
        <div className="flex flex-col gap-4 rounded-lg border border-glass-stroke-subtle p-3">
          <FieldGroup label="Timeout (sec)" hint="0 = no timeout">
            <TextInput type="number" value={String(draft.timeout)}
              onChange={(e) => update("timeout", parseInt(e.target.value, 10) || 0)}
              data-testid="agent-timeout-input" />
          </FieldGroup>

          <FieldGroup label="Environment variables" hint="Injected at runtime. DENKER_* vars are added automatically.">
            <div className="flex flex-col gap-1.5">
              {draft.envVars.map((env, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <TextInput value={env.key} onChange={(e) => updateEnvVar(i, "key", e.target.value)}
                    placeholder="KEY" className="flex-1" data-testid={`agent-env-key-${i}`} />
                  <TextInput value={env.value} onChange={(e) => updateEnvVar(i, "value", e.target.value)}
                    placeholder="value" className="flex-1" data-testid={`agent-env-value-${i}`} />
                  <button type="button" onClick={() => removeEnvVar(i)}
                    className="flex h-[34px] w-8 items-center justify-center rounded-lg text-muted transition-colors hover:text-red-400"
                    data-testid={`agent-env-remove-${i}`}
                  >
                    <Icons.X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <Button variant="ghost" size="sm" onClick={addEnvVar} data-testid="agent-env-add-btn">
                <Icons.Plus className="h-3 w-3" />
                Add variable
              </Button>
            </div>
          </FieldGroup>
        </div>
      )}

      {/* ── Actions ── */}
      <div className="flex items-center gap-2 border-t border-glass-stroke-subtle pt-4" data-testid="agent-detail-actions">
        <Button variant="secondary" size="sm" onClick={onBack} data-testid="agent-cancel-btn">
          Cancel
        </Button>
        <Button variant="default" size="sm" onClick={() => onSave(draft)} data-testid="agent-save-btn">
          Save
        </Button>
        <div className="flex-1" />
        <Button variant="destructive" size="sm" onClick={() => onDelete(draft.id)} data-testid="agent-delete-btn">
          <Icons.Trash2 className="h-3 w-3" />
          Delete
        </Button>
      </div>
    </div>
  );
}

// ─── Main Section ───────────────────────────────────────────────────────────

interface AgentsSectionProps {
  className?: string;
  "data-testid"?: string;
}

export function AgentsSection({ className, "data-testid": testId = "settings-agents" }: AgentsSectionProps) {
  const [agents, setAgents] = useState<Agent[]>(MOCK_AGENTS);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedAgent = selectedId ? (agents.find((a) => a.id === selectedId) ?? null) : null;

  function handleCreateAgent() {
    const newId = `a${Date.now()}`;
    const newAgent: Agent = {
      id: newId, name: "New Agent", role: "", capabilities: "",
      systemPrompt: "", model: "sonnet", thinkingEffort: "auto",
      maxTurns: 80, tools: MOCK_TOOLS.map((t) => ({ ...t, enabled: ["canvas", "bridge"].includes(t.id) })),
      skills: [MOCK_SKILLS[0]!], heartbeatEnabled: false, heartbeatInterval: 3600,
      timeout: 0, envVars: [], status: "active", color: "#6B7280", letter: "N",
    };
    setAgents((prev) => [...prev, newAgent]);
    setSelectedId(newId);
  }

  function handleSave(updated: Agent) {
    setAgents((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    setSelectedId(null);
  }

  function handleDelete(id: string) {
    setAgents((prev) => prev.filter((a) => a.id !== id));
    setSelectedId(null);
  }

  return (
    <div className={cn("flex flex-col", className)} data-testid={testId}>
      {selectedAgent ? (
        <AgentDetail agent={selectedAgent} onBack={() => setSelectedId(null)}
          onSave={handleSave} onDelete={handleDelete} data-testid="agent-detail" />
      ) : (
        <AgentList agents={agents} onSelect={setSelectedId}
          onCreateAgent={handleCreateAgent} data-testid="agent-list" />
      )}
    </div>
  );
}

export type { AgentsSectionProps };
